import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Shield, Check, X, Trash2, RotateCcw, Clock, AlertTriangle, ChevronDown } from "lucide-react";
import { ApprovalPreview } from "@/components/ApprovalPreview";

export default function ApprovalsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const [approvals, setApprovals] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDenyModal, setShowDenyModal] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [denyReason, setDenyReason] = useState("");

  const [pendingEdits, setPendingEdits] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [appRes, trashRes, editsRes] = await Promise.all([
        axios.get(`${API}/approvals`),
        isOwner ? axios.get(`${API}/trash`) : Promise.resolve({ data: [] }),
        isOwner ? axios.get(`${API}/timecard/pending-edits`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setApprovals(appRes.data || []);
      setTrash(trashRes.data || []);
      setPendingEdits(editsRes.data || []);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      await axios.post(`${API}/approvals/${approvalId}/approve`);
      toast.success("Request approved");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
  };

  const handleDeny = async () => {
    if (!showDenyModal) return;
    try {
      await axios.post(`${API}/approvals/${showDenyModal}/deny`, { reason: denyReason });
      toast.success("Request denied");
      setShowDenyModal(null);
      setDenyReason("");
      fetchData();
    } catch (err) {
      toast.error("Failed to deny");
    }
  };

  const handleApproveEdit = async (entryId) => {
    try {
      await axios.post(`${API}/timecard/pending-edits/${entryId}/approve`);
      toast.success("Edit approved and applied");
      fetchData();
    } catch (err) {
      toast.error("Failed to approve edit");
    }
  };

  const handleDenyEdit = async (entryId) => {
    try {
      await axios.post(`${API}/timecard/pending-edits/${entryId}/deny`, { reason: denyReason });
      toast.success("Edit denied");
      setShowDenyModal(null);
      setDenyReason("");
      fetchData();
    } catch (err) {
      toast.error("Failed to deny edit");
    }
  };

  const handleRestore = async (trashId) => {    try {
      await axios.post(`${API}/trash/${trashId}/restore`);
      toast.success("Record restored!");
      fetchData();
    } catch (err) {
      toast.error("Failed to restore");
    }
  };

  const handlePermanentDelete = async (trashId) => {
    if (!window.confirm("Permanently delete? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/trash/${trashId}`);
      toast.success("Permanently deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approvals-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" /> Approvals & Trash
        </h1>
      </div>

      <Tabs defaultValue="approvals">
        <TabsList>
          <TabsTrigger value="approvals">
            Pending Approvals
            {approvals.filter(a => a.status === "pending").length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs">{approvals.filter(a => a.status === "pending").length}</Badge>
            )}
          </TabsTrigger>
          {isOwner && (
            <TabsTrigger value="timecard-edits">
              Timecard Edits
              {pendingEdits.length > 0 && <Badge className="ml-2 bg-amber-500 text-white text-xs">{pendingEdits.length}</Badge>}
            </TabsTrigger>
          )}
          {isOwner && <TabsTrigger value="trash">Trash ({trash.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="approvals" className="space-y-3 mt-4">
          {approvals.length > 0 ? approvals.map(a => (
            <Card key={a.approval_id} className={`cursor-pointer transition-shadow hover:shadow-md ${a.status === "pending" ? "border-amber-300" : ""} ${expanded === a.approval_id ? "ring-2 ring-indigo-200" : ""}`} data-testid={`approval-${a.approval_id}`}
              onClick={() => setExpanded(expanded === a.approval_id ? null : a.approval_id)}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === a.approval_id ? "rotate-180" : ""}`} />
                      <Badge className={`text-xs ${a.action === "delete" ? "bg-red-100 text-red-700" : a.action === "change_order" ? "bg-indigo-100 text-indigo-700" : a.action === "late_notice" ? "bg-red-100 text-red-800" : a.action === "send_po" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                        {a.action === "delete" ? "Delete" : a.action === "change_order" ? "Change Order" : a.action === "late_notice" ? "Late Notice" : a.action === "send_po" ? "Send Purchase Order" : "Edit"}
                      </Badge>
                      <Badge variant="outline" className="text-xs capitalize">{a.collection?.replace("_", " ")}</Badge>
                      <Badge className={`text-xs ${a.status === "pending" ? "bg-amber-100 text-amber-700" : a.status === "approved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {a.status}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium text-slate-800">{a.description}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      By {a.requester?.name || "Unknown"} ({a.requester?.role}) — {new Date(a.created_at).toLocaleString()}
                    </p>
                    {a.deny_reason && <p className="text-xs text-red-600 mt-1">Reason: {a.deny_reason}</p>}
                    {expanded !== a.approval_id && <p className="text-[11px] text-indigo-500 mt-1">Click to see what changes</p>}
                  </div>
                  {isOwner && a.status === "pending" && (
                    <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => handleApprove(a.approval_id)}>
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8" onClick={() => setShowDenyModal(a.approval_id)}>
                        <X className="w-3 h-3 mr-1" /> Deny
                      </Button>
                    </div>
                  )}
                </div>
                {expanded === a.approval_id && <div onClick={(e) => e.stopPropagation()}><ApprovalPreview approvalId={a.approval_id} /></div>}
              </CardContent>
            </Card>
          )) : (
            <Card><CardContent className="py-12 text-center">
              <Check className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-slate-500">No pending approvals</p>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* Timecard Edits Tab */}
        {isOwner && (
          <TabsContent value="timecard-edits" className="space-y-3 mt-4">
            {pendingEdits.length > 0 ? pendingEdits.map(edit => (
              <Card key={edit.entry_id} className="border-amber-300" data-testid={`edit-${edit.entry_id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Timecard Edit</Badge>
                        <span className="text-sm font-medium">{edit.user_name}</span>
                        <span className="text-xs text-slate-400">{new Date(edit.created_at).toLocaleString()}</span>
                      </div>
                      {/* Side by side comparison */}
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-red-600 mb-1">Original</p>
                          <p>Date: {edit.original?.date}</p>
                          <p>Time: {edit.original?.start_time} - {edit.original?.end_time}</p>
                          <p>Hours: <span className="font-bold">{edit.original?.total_hours}h</span></p>
                          {edit.original?.notes && <p className="text-xs text-slate-500 mt-1">"{edit.original.notes}"</p>}
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-green-600 mb-1">Proposed</p>
                          <p>Date: {edit.proposed?.date || edit.original?.date}</p>
                          <p>Time: {edit.proposed?.start_time || edit.original?.start_time} - {edit.proposed?.end_time || edit.original?.end_time}</p>
                          <p>Hours: <span className="font-bold">{edit.proposed?.total_hours || "?"}h</span></p>
                          {edit.proposed?.notes && <p className="text-xs text-slate-500 mt-1">"{edit.proposed.notes}"</p>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => handleApproveEdit(edit.entry_id)}>
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8" onClick={() => setShowDenyModal(`edit_${edit.entry_id}`)}>
                        <X className="w-3 h-3 mr-1" /> Deny
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card><CardContent className="py-12 text-center">
                <Check className="w-10 h-10 text-green-300 mx-auto mb-3" />
                <p className="text-slate-500">No pending timecard edits</p>
              </CardContent></Card>
            )}
          </TabsContent>
        )}

        {isOwner && (
          <TabsContent value="trash" className="space-y-3 mt-4">
            {trash.length > 0 ? trash.map(item => (
              <Card key={item.trash_id} data-testid={`trash-${item.trash_id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs capitalize">{item.original_collection?.replace("_", " ")}</Badge>
                        <span className="text-xs text-slate-400">ID: {item.original_id?.slice(-8)}</span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">
                        {item.record?.title || item.record?.name || item.record?.first_name || item.record?.customer_name || "Record"}
                      </p>
                      <p className="text-xs text-slate-500">
                        Deleted by {item.deleted_by_name} — {new Date(item.deleted_at).toLocaleString()}
                      </p>
                      <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        Auto-purges {new Date(item.auto_purge_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" className="text-green-600 border-green-200 h-8" onClick={() => handleRestore(item.trash_id)}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Restore
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-8" onClick={() => handlePermanentDelete(item.trash_id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card><CardContent className="py-12 text-center">
                <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Trash is empty</p>
                <p className="text-sm text-slate-400">Deleted items appear here for 30 days</p>
              </CardContent></Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Deny Modal */}
      <Dialog open={!!showDenyModal} onOpenChange={(v) => { if (!v) { setShowDenyModal(null); setDenyReason(""); } }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-sm">
          <DialogHeader><DialogTitle>Deny Request</DialogTitle></DialogHeader>
          <Textarea value={denyReason} onChange={(e) => setDenyReason(e.target.value)} rows={3} placeholder="Reason for denying (optional)..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDenyModal(null)}>Cancel</Button>
            <Button onClick={handleDeny} className="bg-red-600 hover:bg-red-700">Deny Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
